import Kalpa from 'kalpa-tree';
import * as d3 from 'd3-selection'
import { EventEmitter } from 'events'
import stream from 'stream-array'
import Contents from './lib/contents.js'
import contentWidth from './lib/content-width.js'
import regexEscape from 'escape-string-regexp'
import 'd3-transition'

const MIN_COLUMN_WIDTH = 50

class RecordList extends EventEmitter {

  constructor (container, nodes, config) {
    super()

    this.isRtl = document.documentElement.dir === 'rtl'
    this.container = container
    this.nodes = nodes
    this.config = config

    // An array of column indices that are editable
    this.config.editableColumns = this.config.editableColumns || []

    this.contents = new Contents(config)

    this._headerWidths = [] // The width of each header column, these are calculated once, when we render the header

    // Bind these functions to the record list instead of d3's `this`
    this._header = this._header.bind(this)
    this._tree = this._tree.bind(this)
  }

  /*
   * Builds the header
   */
  _header (selection, config) {
    const headerWidths = this._headerWidths

    selection.each(function () {
      let columns = config.columns.slice(0) // Copy b/c we may add an empty header for the row action column

      // Build the header
      let header = d3.select(this)
                     .selectAll('.tree-container')
                     .insert('div', ':first-child')
                     .attr('class', 'record-list-header')

      header.selectAll('.column-header')
            .data(columns)
            .enter()
              .append('div')
              .attr('class', 'column-header')
              .classed('column-header-0', (d, i) => i === 0)
              .html(d => d.label)

      // We need to add an empty header for the row action column
      if (config.rowAction) {
        header.insert('div', ':first-child')
              .attr('class', 'column-header row-action-column')
              .html('')
      }

      // Calculate the width of each header column on the initial render
      header.selectAll('.column-header')
            .each(function (d, i) {
              headerWidths[i] = contentWidth(this)
            })

    })
  }

  /*
   * Builds the tree section
   */
  _tree (selection) {
    let tree = new Kalpa({
      stream: stream(this.nodes), // The tree expects a stream, so convert the nodes array
      forest: true,
      performanceThreshold: this.config.performanceThreshold ?? 50, // Only render nodes that are in view, this is important for record-lists since the tree nodes commonly contain complex dom structures. See Impact #59229
      movable: () => false, // Never allow nodes to be moved
      indentableSelector: '.column-0 .node-contents',
      contents: this.contents.render.bind(this),
      scrollableContainer: () => {
        // We use a `position: sticky` header, which means the main record-list
        // container will handle scrolling, overriding the tree's default scroll container
        return this.container.querySelector('.record-list-container')
      }
    })

    this.tree = tree

    // When the tree changes height, we need to update our container's height
    this.tree.on('change:height', height => {
      window.requestAnimationFrame(() => {
        if (height !== 'auto') {
          // If the height is set to an explicit value, use it since the tree
          // is performance tuned and not drawing all nodes
          this.tree.el.style('height', `${this.tree.el.node().scrollHeight}px`)
        } else {
          // Set the height based on how many nodes are drawn in the tree
          let count = this.tree.el.selectAll('.tree ul li:not(.outgoing-node)').data().length
          this.tree.el.style('height', `${count * this.tree.options.height}px`)
        }
        this.setColumnWidths()
      })
    })

    this.tree.on('rebind:exit', this.exitingNodes.bind(this))

    // When the tree is first rendered, render column widths so
    // the top header row has accurate widths
    this.tree.on('rendered', () => {
      // Now set the proper column widths
      this.setColumnWidths()
    })

    selection.each(function () {
      this.append(tree.render().el.node())
      tree.el.classed('tree-container-light', true)
    })
  }

  /*
   * Fired when a node changes state and we need to update the state of the
   * record list
   */
  change (id) {
    let self = this
    this.tree.node.filter(d => d.id == id).each(function (node) {
      self.persistState(self.tree.get(node.id), this)
    })
  }

  /*
   * Persist the current state of all columns within the tree node.
   * Receives the tree node data object and the tree li `el`
   */
  persistState (node, el) {
    let readFromDom = this.config.readFromDom

    if (!readFromDom) {
      return
    }

    d3.select(el)
      .selectAll('.column.column-editable:not(.row-action-column)')
      .each(function (d) {
        // Update the the original node with the current value in the DOM
        node.columns[d.idx] = readFromDom(this)
      })
  }

  /*
   * Node(s) are being removed from the tree. Update
   * the columns state based on their DOM values by invoking
   * the supplied readFromDom method
   */
  exitingNodes (selection) {
    selection.each(node => {
      this.emit('node-removed', this.tree.get(node.id))
      this.change(node.id)
    })
  }

  processRows (columns) {
    const self = this

    // Now each row (which is a node in the tree)
    this.tree.el.selectAll('.node:not(.outgoing-node)').each(function () {
      d3.select(this).selectAll('.column').each(function (d, idx) {
        const width = contentWidth(this)
        const offset = +this.dataset.xOffset || 0
        columns[idx] = Math.max(columns[idx], width + offset)
      })
    })
  }

  setColumnWidths () {
    // Initialize columns as the header widths
    const columns = this._headerWidths.slice(0)

    this.tree.el.selectAll('.node:not(.outgoing-node)').each(function (d) {
      columns.forEach((_, idx) => {
        columns[idx] = Math.max(MIN_COLUMN_WIDTH, columns[idx], d._columnWidths[idx])
      })
    })

    this.container.style.setProperty('--gridTemplateColumns', columns.map(px => `${px}px`).join(' '))
  }

  render () {
    let container = d3.select(this.container)
                       .append('div')
                       .attr('class', 'record-list-container')
                       .classed('has-action-column', !!this.config.rowAction)

    // Set up a scroll listener to readjust the tree's viewport
    container.on('scroll', () => {
      let scroll = container.node().scrollTop
      if (!this._scrollTop) {
        this._scrollTop = scroll
      }

      if (Math.abs(scroll - this._scrollTop) > this.tree.options.height) {
        this.tree.adjustViewport()
        this._scrollTop = scroll
      }
     }, { passive: true }) // We'll adjust viewport after the browser has finished its native scrolling

    // Build the components
    container.call(this._tree)
             .call(this._header, this.config)

    return this
  }

  resize () {
    this.setColumnWidths()
  }

  /*
   * Search the records list for the given term. By default searches all `columns`
   * and the `id` of the node
   *
   * Optionally pass a filter function that receives the search term and the node
   */
  search (term, filter) {
    if (!filter) {
      let re = new RegExp(regexEscape(term) || '', 'i')

      filter = (term, node) => {
        return re.test(node.id.toString()) || !!node.columns.find(col => re.test(col))
      }
    }
    if (!term) {
      return this.tree.search(null)
    }

    this.tree.filter(filter.bind(this.tree, term)) // Run normal tree filter code
    this.tree.adjustViewport() // Make sure the filtered results are in view
    return this.tree
  }

}

export default RecordList
